import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Input, Select, InputNumber, Switch, DatePicker, message } from "antd";
import dayjs from "dayjs";
import authApi from "../../../api/authApi";

const CAMP_OPTIONS = [
    { value: "shopee", label: "Shopee" },
    { value: "lazada", label: "Lazada" },
    { value: "tiktok", label: "TikTok" },
];

const EMPTY_FORM = {
    name: "",
    campId: "shopee",
    multiplier: 2,
    enabled: true,
    // dateRange holds [dayjs|null, dayjs|null] for the RangePicker
    dateRange: [null, null],
};

function PromoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (isEdit) fetchPromo();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchPromo = async () => {
        try {
            const { data } = await authApi.getPromos();
            const promo = (data.promos || []).find((p) => String(p.id) === String(id));
            if (!promo) {
                message.error("Không tìm thấy chiến dịch");
                navigate("/app-rate-promo");
                return;
            }
            setForm({
                name: promo.name || "",
                campId: promo.campId || "shopee",
                multiplier: Number(promo.multiplier) || 2,
                enabled: !!promo.enabled,
                dateRange: [
                    promo.startTime ? dayjs.unix(Number(promo.startTime)) : null,
                    promo.endTime   ? dayjs.unix(Number(promo.endTime))   : null,
                ],
            });
        } catch {
            message.error("Không thể tải thông tin chiến dịch");
            navigate("/app-rate-promo");
        }
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            message.error("Vui lòng nhập tên chiến dịch");
            return;
        }
        if (!form.campId) {
            message.error("Vui lòng chọn sàn");
            return;
        }
        if (!form.multiplier || form.multiplier < 1) {
            message.error("Hệ số phải ≥ 1");
            return;
        }

        const payload = {
            name:       form.name.trim(),
            campId:     form.campId,
            multiplier: form.multiplier,
            enabled:    form.enabled,
            startTime:  form.dateRange[0] ? form.dateRange[0].unix() : null,
            endTime:    form.dateRange[1] ? form.dateRange[1].unix() : null,
        };

        setLoading(true);
        try {
            if (isEdit) {
                await authApi.updatePromo(id, payload);
                message.success("Cập nhật chiến dịch thành công");
            } else {
                await authApi.createPromo(payload);
                message.success("Tạo chiến dịch thành công");
            }
            navigate("/app-rate-promo");
        } catch (err) {
            const msg = err?.response?.data?.message || "Có lỗi xảy ra";
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    return (
        <div className="p-6 max-w-xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">
                {isEdit ? "Sửa Chiến dịch X2" : "Tạo Chiến dịch X2 Mới"}
            </h1>

            <div className="space-y-5">
                {/* Tên */}
                <div>
                    <label className="block mb-1 font-medium">Tên chiến dịch *</label>
                    <Input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="VD: X2 Shopee Tháng 6"
                    />
                </div>

                {/* Sàn */}
                <div>
                    <label className="block mb-1 font-medium">Sàn *</label>
                    <Select
                        value={form.campId}
                        onChange={(v) => set("campId", v)}
                        className="w-full"
                        options={CAMP_OPTIONS}
                    />
                </div>

                {/* Hệ số */}
                <div>
                    <label className="block mb-1 font-medium">Hệ số hoàn tiền *</label>
                    <InputNumber
                        min={1}
                        step={0.5}
                        value={form.multiplier}
                        onChange={(v) => set("multiplier", v)}
                        addonAfter="×"
                        style={{ width: 160 }}
                    />
                    <p className="text-xs text-content-3 mt-1">
                        Ví dụ: 2 = X2 hoàn tiền, 3 = X3 hoàn tiền
                    </p>
                </div>

                {/* Bật/Tắt */}
                <div className="flex items-center gap-3">
                    <label className="font-medium">Bật chiến dịch</label>
                    <Switch
                        checked={form.enabled}
                        onChange={(v) => set("enabled", v)}
                        checkedChildren="BẬT"
                        unCheckedChildren="Tắt"
                    />
                </div>

                {/* Cửa sổ thời gian */}
                <div>
                    <label className="block mb-1 font-medium">
                        Cửa sổ thời gian đặt hàng (tuỳ chọn)
                    </label>
                    <DatePicker.RangePicker
                        showTime={{ format: "HH:mm:ss" }}
                        format="DD/MM/YYYY HH:mm:ss"
                        value={form.dateRange}
                        onChange={(vals) =>
                            set("dateRange", vals ? [vals[0], vals[1]] : [null, null])
                        }
                        className="w-full"
                        placeholder={["Bắt đầu", "Kết thúc"]}
                    />
                    <p className="text-xs text-content-3 mt-1">
                        Để trống = không giới hạn thời gian.
                        Đơn hàng có purchaseTime trong khoảng này mới được áp hệ số.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                    <Button type="primary" onClick={handleSubmit} loading={loading}>
                        {isEdit ? "Cập nhật" : "Tạo mới"}
                    </Button>
                    <Button onClick={() => navigate("/app-rate-promo")}>Huỷ</Button>
                </div>
            </div>
        </div>
    );
}

export default PromoForm;
